import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import * as AssessmentService from '@services/AssessmentService';
import { useAppTheme } from '@contexts/ThemeContext';
import { Users, FileText, ClipboardCheck, Plus, Edit, PlusCircle, ArrowRight } from 'lucide-react';
import Spinner from './components/Spinner';
import './AssessmentDashboard.css';

const AssessmentDashboard: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAssessments: 0,
    totalResults: 0
  });
  const [publishedAssessments, setPublishedAssessments] = useState<AssessmentService.Assessment[]>([]);
  const [draftAssessments, setDraftAssessments] = useState<AssessmentService.Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch stats and assessment types in parallel
      const [statsResponse, publishedResponse, draftResponse] = await Promise.all([
        AssessmentService.getAdminStats(),
        AssessmentService.getAssessments(),
        AssessmentService.getDraftAssessments()
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      } else {
        throw new Error(statsResponse.error?.message || 'Failed to fetch stats');
      }

      if (publishedResponse.success && publishedResponse.data) {
        setPublishedAssessments(publishedResponse.data);
      } else {
        console.error('Failed to fetch published assessments:', publishedResponse.error?.message);
      }

      if (draftResponse.success && draftResponse.data) {
        setDraftAssessments(draftResponse.data);
      } else {
        console.error('Failed to fetch draft assessments:', draftResponse.error?.message);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResults = (assessmentId: number) => {
    navigate(`/assessment-results/${assessmentId}`);
  };

  const handleCreateAssessment = () => {
    navigate('/create-assessment');
  };

  const handleFinishAssessment = (assessmentId: number) => {
    // Navigate to module-assessment-mapping with the assessmentId
    navigate(`/module-assessment-mapping?assessmentId=${assessmentId}`);
  };

  const handleCreateQuestions = (assessmentId: number, assessmentTypeId?: number) => {
    // Navigate to create-question page
    // The CreateQuestion component handles both regular and 360 feedback questions
    // based on assessmentTypeId (shows Create360Question when assessmentTypeId === 3)
    navigate(`/create-question?assessmentId=${assessmentId}`);
  };

  const handleViewAllPublished = () => {
    navigate('/manage-assessments?tab=published');
  };

  const handleViewAllDraft = () => {
    navigate('/manage-assessments?tab=draft');
  };

  const renderAssessmentCard = (assessment: AssessmentService.Assessment, isDraft: boolean = false) => {
    return (
      <div key={assessment.assessmentId} className="dashboard-page-assessment-card">
        <div className="dashboard-page-assessment-card-header">
          <h3 className="dashboard-page-assessment-title">{assessment.assessmentTitle}</h3>
          {isDraft && (
            <span className="dashboard-page-status-badge dashboard-page-status-draft">Draft</span>
          )}
        </div>
        <div className="dashboard-page-assessment-card-body">
          {assessment.description && (
            <p className="dashboard-page-assessment-description">{assessment.description}</p>
          )}
        </div>
        <div className="dashboard-page-assessment-card-footer">
          {!isDraft && (
            <button
              className="dashboard-page-btn-view-results"
              onClick={() => handleViewResults(assessment.assessmentId)}
            >
              View Results
            </button>
          )}
          {isDraft && (
            <div className="dashboard-page-draft-actions">
              <button
                className="dashboard-page-btn-finish-assessment"
                onClick={() => handleFinishAssessment(assessment.assessmentId)}
              >
                <Edit size={18} />
                <span>Modules</span>
              </button>
              <button
                className="dashboard-page-btn-create-questions"
                onClick={() => handleCreateQuestions(assessment.assessmentId, assessment.assessment_type_id)}
              >
                <PlusCircle size={18} />
                <span>Questions</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="dashboard-page-assessment-dashboard" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="dashboard-page-assessment-dashboard" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="dashboard-page-error-message">{error}</div>
          <button className="dashboard-page-btn-retry" onClick={fetchDashboardData}>
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard-page-assessment-dashboard" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="dashboard-page-header">
          <div>
            <h1 className="dashboard-page-title">Assessment Dashboard</h1>
            <p className="dashboard-page-subtitle">Manage and monitor all assessments</p>
          </div>
          <button className="dashboard-page-btn-create-assessment" onClick={handleCreateAssessment}>
            <Plus size={20} />
            <span>Create Assessment</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="dashboard-page-stats-grid">
          <div className="dashboard-page-stat-card dashboard-page-stat-card-blue">
            <div className="dashboard-page-stat-icon">
              <Users size={32} />
            </div>
            <div className="dashboard-page-stat-content">
              <p className="dashboard-page-stat-label">Total Users</p>
              <h2 className="dashboard-page-stat-value">{stats.totalUsers}</h2>
            </div>
          </div>

          <div className="dashboard-page-stat-card dashboard-page-stat-card-green">
            <div className="dashboard-page-stat-icon">
              <FileText size={32} />
            </div>
            <div className="dashboard-page-stat-content">
              <p className="dashboard-page-stat-label">Total Assessments</p>
              <h2 className="dashboard-page-stat-value">{stats.totalAssessments}</h2>
            </div>
          </div>

          <div className="dashboard-page-stat-card dashboard-page-stat-card-purple">
            <div className="dashboard-page-stat-icon">
              <ClipboardCheck size={32} />
            </div>
            <div className="dashboard-page-stat-content">
              <p className="dashboard-page-stat-label">Total Submissions</p>
              <h2 className="dashboard-page-stat-value">{stats.totalResults}</h2>
            </div>
          </div>
        </div>

        {/* Assessments Boxes */}
        <div className="dashboard-page-assessments-boxes">
          {/* Published Assessments Box */}
          <div className="dashboard-page-assessment-box">
            <div className="dashboard-page-box-header">
              <h2 className="dashboard-page-box-title">Published Assessments</h2>
              <button className="dashboard-page-btn-view-all" onClick={handleViewAllPublished}>
                <span>View All</span>
                <ArrowRight size={16} />
              </button>
            </div>
            <div className="dashboard-page-box-content">
              {publishedAssessments.length === 0 ? (
                <div className="dashboard-page-empty-state">
                  <p>No published assessments found</p>
                </div>
              ) : (
                <div className="dashboard-page-assessments-list">
                  {publishedAssessments.slice(0, 3).map((assessment) => renderAssessmentCard(assessment, false))}
                </div>
              )}
            </div>
          </div>

          {/* Draft Assessments Box */}
          <div className="dashboard-page-assessment-box">
            <div className="dashboard-page-box-header">
              <h2 className="dashboard-page-box-title">Draft Assessments</h2>
              <button className="dashboard-page-btn-view-all" onClick={handleViewAllDraft}>
                <span>View All</span>
                <ArrowRight size={16} />
              </button>
            </div>
            <div className="dashboard-page-box-content">
              {draftAssessments.length === 0 ? (
                <div className="dashboard-page-empty-state">
                  <p>No draft assessments found</p>
                </div>
              ) : (
                <div className="dashboard-page-assessments-list">
                  {draftAssessments.slice(0, 3).map((assessment) => renderAssessmentCard(assessment, true))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AssessmentDashboard;
